import React from 'react';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import { OrderInfo, CostBreakdown } from '../types/budget';
import { OrderInfoForm } from '../components/OrderInfoForm';
import { CostBreakdownForm } from '../components/CostBreakdownForm';
import { FinancialSummary } from '../components/FinancialSummary';
import { DataVisualization } from '../components/DataVisualization';
import { MaterialConsumption } from '../components/MaterialConsumption';
import { ActionButtons } from '../components/ActionButtons';
import { useAuth } from '../contexts/AuthContext';

interface DashboardPageProps {
  tabValue: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  orderInfo: any;
  onOrderInfoChange: (field: keyof OrderInfo, value: any) => void;
  costBreakdown: any;
  onCostBreakdownChange: (category: keyof CostBreakdown, items: any[]) => void;
  financialSummary: any;
  onNewPlan: () => void;
  onRefresh: () => void;
  onImport: () => void;
  onExport: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({
  tabValue,
  onTabChange,
  orderInfo,
  onOrderInfoChange,
  costBreakdown,
  onCostBreakdownChange,
  financialSummary,
  onNewPlan,
  onRefresh,
  onImport,
  onExport,
  fileInputRef,
  onFileImport,
}) => {
  const { authData, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Budget Planner
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              Welcome, {authData?.username || 'User'}!
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Garment Industry Budget Planning System
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Comprehensive budget planning for garment manufacturing orders
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={onTabChange} centered>
            <Tab label="Order Info" />
            <Tab label="Cost Breakdown" />
            <Tab label="Material Consumption" />
            <Tab label="Financial Summary" />
            <Tab label="Data Visualization" />
          </Tabs>
        </Box>

        <ActionButtons
          onNewPlan={onNewPlan}
          onRefresh={onRefresh}
          onImport={onImport}
          onExport={onExport}
          fileInputRef={fileInputRef}
          onFileImport={onFileImport}
        />

        {tabValue === 0 && (
          <OrderInfoForm
            orderInfo={orderInfo}
            onChange={onOrderInfoChange}
          />
        )}

        {tabValue === 1 && (
          <CostBreakdownForm
            costBreakdown={costBreakdown}
            onChange={onCostBreakdownChange}
          />
        )}

        {tabValue === 2 && (
          <MaterialConsumption
            orderQty={orderInfo.expQty}
            yarnItems={costBreakdown.yarn}
          />
        )}

        {tabValue === 3 && (
          <FinancialSummary
            orderInfo={orderInfo}
            financialSummary={financialSummary}
          />
        )}

        {tabValue === 4 && (
          <DataVisualization
            costBreakdown={costBreakdown}
            financialSummary={financialSummary}
          />
        )}
      </Container>
    </Box>
  );
};
